import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { templateDetailParams } from 'api';
import { TemplatePreviewModal } from 'components/template/template-preview-modal';
import { useStore } from 'contexts';

type ModalContextValue = {
	template: any;
	templates: any[];
	open: boolean;
	loading: boolean;
	error: any;
	detailsOpen: boolean;
	index: number;
	origin: string;
	setDetailsOpen: (open: boolean) => void;
	openTemplate: (event: any, template: any, templates?: any[]) => void;
	closeTemplate: () => void;
	previousTemplate: () => void;
	nextTemplate: () => void;
	refreshTemplate: () => void;
	updateTemplate: (data: any) => void;
};

export const ModalContext = React.createContext<ModalContextValue | null>(null);

const ModalProvider = ({ children }) => {
	const [template, setTemplate] = useState<any>({});
	const [templates, setTemplates] = useState<any[]>([]);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [origin, setOrigin] = useState('');
	const requestRef = useRef(0);
	const triggerRef = useRef<HTMLElement | null>(null);
	const router = useRouter();
	const store = useStore();
	const templateId = typeof router.query.dataTemplateId === 'string' ? router.query.dataTemplateId : '';
	const index = templates.findIndex(value => value.uuid === template.uuid);

	// Hooks
	useEffect(() => {
		if (!templateId) {
			setOpen(false);
			return;
		}
		setOpen(true);
		fetchTemplate(templateId);
	}, [templateId]);

	// Actions
	const fetchTemplate = async uuid => {
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		try {
			const response = await store.queryRecord({ url: `/templates/${uuid}`, params: templateDetailParams });
			if (request === requestRef.current) setTemplate(response.data || {});
		} catch (nextError) {
			if (request === requestRef.current) setError(nextError);
		} finally {
			if (request === requestRef.current) setLoading(false);
		}
	};

	// Methods
	const previewRoute = uuid => ({
		internal: { pathname: router.pathname, query: { ...router.query, dataTemplateId: uuid } },
		public: `/explore/projects/${uuid}`,
	});
	const openTemplate = (event, nextTemplate, sourceTemplates = []) => {
		if (
			event?.defaultPrevented ||
			event?.button > 0 ||
			event?.metaKey ||
			event?.ctrlKey ||
			event?.shiftKey ||
			event?.altKey ||
			!nextTemplate?.uuid
		)
			return;
		event?.preventDefault();
		triggerRef.current = event?.currentTarget || null;
		setOrigin(router.asPath);
		setTemplate(nextTemplate);
		setTemplates(sourceTemplates.length ? sourceTemplates : [nextTemplate]);
		setDetailsOpen(false);
		setOpen(true);
		const route = previewRoute(nextTemplate.uuid);
		router.push(route.internal, route.public, { shallow: true });
	};
	const closeTemplate = () => {
		requestRef.current += 1;
		setOpen(false);
		router.back();
		window.setTimeout(() => triggerRef.current?.focus(), 0);
	};
	const navigateTemplate = nextTemplate => {
		if (!nextTemplate?.uuid) return;
		setTemplate(nextTemplate);
		const route = previewRoute(nextTemplate.uuid);
		router.replace(route.internal, route.public, { shallow: true });
	};
	const previousTemplate = () => index > 0 && navigateTemplate(templates[index - 1]);
	const nextTemplate = () => index >= 0 && index < templates.length - 1 && navigateTemplate(templates[index + 1]);
	const refreshTemplate = () => templateId && fetchTemplate(templateId);
	const updateTemplate = data => setTemplate(current => ({ ...current, ...data }));

	// Render
	return (
		<ModalContext.Provider
			value={{
				template,
				templates,
				open,
				loading,
				error,
				detailsOpen,
				index,
				origin,
				setDetailsOpen,
				openTemplate,
				closeTemplate,
				previousTemplate,
				nextTemplate,
				refreshTemplate,
				updateTemplate,
			}}>
			{children}
			<TemplatePreviewModal />
		</ModalContext.Provider>
	);
};

const useModal = () => React.useContext(ModalContext) as ModalContextValue;

export { ModalProvider, useModal };
